import { GS1_AI, buildQualifierDocumentId, parseGS1DigitalLink } from '@myplayer/gs1';
import { SECTOR_TEMPLATES, validateSafeHttpsUrl, } from '@myplayer/schema';
import { WEBRISK_UNAVAILABLE_MESSAGE } from './web-risk-client.js';
import { isEpilBlockedPlatformUrl } from './url-safety.js';
export function doesGtinMatchWorkspacePrefixes(paddedKey, companyPrefixes) {
    const gtin13 = paddedKey.length === 14 && paddedKey.startsWith('0') ? paddedKey.slice(1) : paddedKey;
    return companyPrefixes.some((rawPrefix) => {
        const prefix = rawPrefix.trim();
        if (prefix.length === 0)
            return false;
        const unpaddedPrefix = prefix.length > 1 && prefix.startsWith('0') ? prefix.replace(/^0+/, '') : prefix;
        return (paddedKey.startsWith(prefix) ||
            gtin13.startsWith(prefix) ||
            (unpaddedPrefix.length > 0 && gtin13.startsWith(unpaddedPrefix)));
    });
}
export function isPrivateOrBlockedHost(urlStr, resolverHostnames, blockedHosts, unsafeUrls) {
    const safeCheck = validateSafeHttpsUrl(urlStr);
    if (!safeCheck.valid) {
        return {
            unsafe: true,
            reason: safeCheck.reason ??
                `Destination URL '${urlStr}' resolves to a forbidden private, loopback, or metadata address (Spec v2.1 §16).`,
            code: 'API_VALIDATION_FAILED',
        };
    }
    const trimmedUrl = urlStr.trim();
    if (unsafeUrls !== undefined) {
        if (unsafeUrls instanceof Map) {
            const threats = unsafeUrls.get(trimmedUrl);
            if (threats !== undefined) {
                const threatLabel = threats.length > 0 ? ` (${threats.join(', ')})` : '';
                return {
                    unsafe: true,
                    reason: `Unsafe URL blocked by Google Web Risk${threatLabel}: '${trimmedUrl}' (Spec v2.1 §20).`,
                    code: 'API_VALIDATION_FAILED',
                };
            }
        }
        else if (unsafeUrls.has(trimmedUrl)) {
            return {
                unsafe: true,
                reason: `Unsafe URL blocked by Google Web Risk: '${trimmedUrl}' (Spec v2.1 §20).`,
                code: 'API_VALIDATION_FAILED',
            };
        }
    }
    const parsed = new URL(urlStr);
    const host = parsed.hostname.toLowerCase().replace(/\.$/, '');
    if (blockedHosts !== undefined && blockedHosts.has(host)) {
        return {
            unsafe: true,
            reason: `Unsafe URL blocked by Google Web Risk — host '${host}' is flagged as unsafe (Spec v2.1 §20).`,
            code: 'API_VALIDATION_FAILED',
        };
    }
    if (resolverHostnames.some((h) => h.toLowerCase().replace(/\.$/, '') === host)) {
        return {
            unsafe: true,
            reason: `Destination URL '${urlStr}' points back to resolver host '${host}' and would cause a redirect loop (Spec v2.1 §5.2).`,
            code: 'API_VALIDATION_FAILED',
        };
    }
    return { unsafe: false };
}
export function buildGs1CheckBlock(aiCode, primaryKey, companyPrefixes = []) {
    const gs1Result = parseGS1DigitalLink(`https://id.gs1.org/${aiCode}/${primaryKey}`);
    if (!gs1Result.ok) {
        const isRcn = gs1Result.errorCode === 'GS1_SYNTAX_RCN_NOT_PERMITTED';
        return {
            checkDigitValid: gs1Result.errorCode !== 'GS1_SYNTAX_INVALID_CHECK_DIGIT',
            rcnStatus: isRcn ? 'RCN_REJECTED' : 'STANDARD',
            gs1Prefix: null,
            keyType: aiCode,
            canonicalKey: primaryKey,
            paddedKey: primaryKey,
            verifiedByChoice: 'UNVERIFIED',
        };
    }
    const paddedKey = gs1Result.value.primaryKey;
    const gtin13Portion = aiCode === GS1_AI.GTIN && paddedKey.length === 14 ? paddedKey.slice(1) : paddedKey;
    const matchedPrefix = companyPrefixes.find((prefix) => gtin13Portion.startsWith(prefix) || paddedKey.startsWith(prefix)) ?? (gtin13Portion.length >= 7 ? gtin13Portion.slice(0, 7) : null);
    return {
        checkDigitValid: true,
        rcnStatus: aiCode === GS1_AI.GTIN || aiCode === GS1_AI.ITIP ? 'STANDARD' : 'NOT_APPLICABLE',
        gs1Prefix: matchedPrefix,
        keyType: aiCode,
        canonicalKey: paddedKey,
        paddedKey,
        verifiedByChoice: companyPrefixes.some((prefix) => gtin13Portion.startsWith(prefix) || paddedKey.startsWith(prefix))
            ? 'VERIFIED'
            : 'UNVERIFIED',
    };
}
export function validateIdentifierCandidate(aiCode, primaryKey, defaultUrl, links, nowIsoOrOptions = new Date().toISOString(), rules = []) {
    const options = typeof nowIsoOrOptions === 'string' ? { nowIso: nowIsoOrOptions } : nowIsoOrOptions;
    const nowIso = options.nowIso ?? new Date().toISOString();
    const resolverHostnames = options.resolverHostnames ?? ['myplayr.cloud', 'id.myplayr.cloud'];
    const epilCustomDomains = [
        ...(options.verifiedCustomDomains ?? []),
        ...(options.resolverHostnames ?? []),
    ];
    const blockedHosts = options.webRiskBlockedHosts;
    const unsafeUrls = options.webRiskUnsafeUrls;
    const errors = [];
    const warnings = [];
    if (options.webRiskUnverified === true) {
        errors.push({
            code: 'API_VALIDATION_FAILED',
            message: WEBRISK_UNAVAILABLE_MESSAGE,
            field: 'defaultUrl',
            clauseRef: 'Spec v2.1 §20 (Google Web Risk Fail-Closed Safety Check)',
        });
    }
    const gs1Result = parseGS1DigitalLink(`https://id.gs1.org/${aiCode}/${primaryKey}`);
    if (!gs1Result.ok) {
        errors.push({
            code: gs1Result.errorCode,
            message: gs1Result.message,
            field: 'primaryKey',
            clauseRef: gs1Result.clauseRef,
        });
    }
    else if (options.workspaceType !== 'RETAILER' &&
        options.enforcePrefixValidation !== false &&
        options.gs1CompanyPrefixes !== undefined &&
        options.gs1CompanyPrefixes.some((p) => p.trim().length > 0) &&
        (aiCode === GS1_AI.GTIN || aiCode === GS1_AI.ITIP)) {
        const padded = gs1Result.value.primaryKey;
        const matchesPrefix = doesGtinMatchWorkspacePrefixes(padded, options.gs1CompanyPrefixes);
        if (!matchesPrefix) {
            errors.push({
                code: 'API_VALIDATION_FAILED',
                message: `GTIN '${padded}' does not match any GS1 Company Prefix registered for this workspace (${options.gs1CompanyPrefixes.join(', ')}).`,
                field: 'primaryKey',
                clauseRef: 'Spec v2.1 §4.1, §6.2 & §21 (GS1 Company Prefix Ownership)',
            });
        }
    }
    const defaultUrlSafety = isPrivateOrBlockedHost(defaultUrl, resolverHostnames, blockedHosts, unsafeUrls);
    if (defaultUrlSafety.unsafe) {
        errors.push({
            code: defaultUrlSafety.code ?? 'API_VALIDATION_FAILED',
            message: defaultUrlSafety.reason ?? 'defaultUrl must use a safe public https:// URL.',
            field: 'defaultUrl',
            clauseRef: 'GS1-Conformant Resolver Standard v1.2.1 §2.5 & Spec v2.1 §20',
        });
    }
    const defaultLinkCount = links.filter((l) => l.defaultLink).length;
    if (defaultLinkCount > 1 ||
        (options.requireSingleDefaultLink === true && defaultLinkCount !== 1)) {
        errors.push({
            code: 'API_VALIDATION_FAILED',
            message: `Exactly one TargetLink per level must have defaultLink = true (found ${String(defaultLinkCount)}).`,
            field: 'links',
            clauseRef: 'Spec v2.1 §4.2 & §6.4 (Single Default Link Invariant)',
        });
    }
    for (const link of links) {
        const linkSafety = isPrivateOrBlockedHost(link.href, resolverHostnames, blockedHosts, unsafeUrls);
        if (linkSafety.unsafe && !(link.href.trim() === defaultUrl.trim() && defaultUrlSafety.unsafe)) {
            errors.push({
                code: linkSafety.code ?? 'API_VALIDATION_FAILED',
                message: `TargetLink href '${link.href}' invalid: ${linkSafety.reason ?? 'must use https://'}.`,
                field: 'links',
                clauseRef: 'GS1-Conformant Resolver Standard v1.2.1 §2.5 & Spec v2.1 §20',
            });
        }
        if (link.linkType === 'gs1:epil' && isEpilBlockedPlatformUrl(link.href, epilCustomDomains)) {
            errors.push({
                code: 'API_VALIDATION_FAILED',
                message: 'Regulated gs1:epil links cannot use Myplayer Projects or platform hosts as a destination (Spec v2.1 §4.2).',
                field: 'links',
                clauseRef: 'Spec v2.1 §4.2 (Healthcare & Pharma Regulated ePIL Constraint)',
            });
        }
    }
    for (const rule of rules) {
        const ruleSafety = isPrivateOrBlockedHost(rule.targetHref, resolverHostnames, blockedHosts, unsafeUrls);
        if (ruleSafety.unsafe) {
            errors.push({
                code: 'API_VALIDATION_FAILED',
                message: `Conditional rule '${rule.id}' targetHref '${rule.targetHref}' invalid: ${ruleSafety.reason ?? 'must use https://'}.`,
                field: 'rules',
                clauseRef: 'Spec v2.1 §5.2 & §20',
            });
        }
        if (rule.condition.validFrom !== undefined &&
            rule.condition.validUntil !== undefined &&
            Date.parse(rule.condition.validFrom) >= Date.parse(rule.condition.validUntil)) {
            errors.push({
                code: 'API_VALIDATION_FAILED',
                message: `Conditional rule '${rule.id}' validFrom (${rule.condition.validFrom}) must be earlier than validUntil (${rule.condition.validUntil}).`,
                field: 'rules',
                clauseRef: 'Spec v2.1 §7.1 Stage 7 (Scheduled Campaign Transition Window)',
            });
        }
    }
    if (options.workspaceSector !== undefined) {
        const template = SECTOR_TEMPLATES[options.workspaceSector];
        if (!template.allowedPrimaryAIs.includes(aiCode)) {
            warnings.push({
                code: 'SECTOR_PRIMARY_AI_MISMATCH',
                message: `AI (${aiCode}) is outside the standard primary AIs for sector ${template.name}.`,
                field: 'ai',
                clauseRef: 'Spec v2.1 §4.2 (Sector Link Slot Templates)',
            });
        }
    }
    return {
        status: errors.length > 0 ? 'INVALID' : warnings.length > 0 ? 'WARNING' : 'VALID',
        validatedAt: nowIso,
        errors,
        warnings,
    };
}
export function validateQualifierCandidate(parentAi, parentPrimaryKey, qualifiers, defaultUrl, links, options = {}, rules = []) {
    const nowIso = options.nowIso ?? new Date().toISOString();
    const errors = [];
    const warnings = [];
    const orderedPairs = [];
    const qualifierOrder = [
        GS1_AI.CPV,
        GS1_AI.BATCH_LOT,
        GS1_AI.SERIAL,
        GS1_AI.TPX,
        GS1_AI.GLN_EXT,
        GS1_AI.UIC_EXT,
        GS1_AI.CPID_SERIAL,
        GS1_AI.SRIN,
        GS1_AI.PAYMENT_REF,
    ];
    for (const qAi of qualifierOrder) {
        const val = qualifiers[qAi];
        if (val !== undefined) {
            orderedPairs.push(`/${qAi}/${encodeURIComponent(val)}`);
        }
    }
    for (const [k, v] of Object.entries(qualifiers)) {
        if (!qualifierOrder.includes(k)) {
            orderedPairs.push(`/${k}/${encodeURIComponent(v)}`);
        }
    }
    if (qualifiers[GS1_AI.SERIAL] !== undefined &&
        (qualifiers[GS1_AI.BATCH_LOT] !== undefined || qualifiers[GS1_AI.CPV] !== undefined)) {
        errors.push({
            code: 'API_VALIDATION_FAILED',
            message: 'Qualifier document for serial (AI 21) SHALL NOT also bind CPV (AI 22) or Batch/Lot (AI 10) (GS1-Conformant Resolver Standard v1.2.1 §2.5.10).',
            field: 'qualifiers',
            clauseRef: 'GS1-Conformant Resolver Standard v1.2.1 §2.5.10',
        });
    }
    const candidateUri = `https://id.gs1.org/${parentAi}/${parentPrimaryKey}${orderedPairs.join('')}`;
    const parsed = parseGS1DigitalLink(candidateUri);
    if (!parsed.ok) {
        errors.push({
            code: parsed.errorCode,
            message: parsed.message,
            field: 'qualifiers',
            clauseRef: parsed.clauseRef,
        });
    }
    const baseCheck = validateIdentifierCandidate(parentAi, parentPrimaryKey, defaultUrl, links, options, rules);
    errors.push(...baseCheck.errors);
    warnings.push(...baseCheck.warnings);
    return {
        status: errors.length > 0 ? 'INVALID' : warnings.length > 0 ? 'WARNING' : 'VALID',
        validatedAt: nowIso,
        errors,
        warnings,
    };
}
export function buildGranularQualifierKey(combinationCode, qualifiers) {
    return buildQualifierDocumentId(combinationCode, qualifiers);
}
//# sourceMappingURL=identifier-validator.js.map